import { z } from 'zod';

export const snowflakeOwnerSchema = z.object({
  externalId: z.string().min(1),
  name: z.string().min(1).max(255),
  state: z.string().min(1).max(50),
});

export const snowflakeVendorSchema = z.object({
  externalId: z.string().min(1),
  name: z.string().min(1).max(255),
  state: z.string().min(1).max(50),
  ownerExternalId: z.string().min(1),
});

export const snowflakeLettingSchema = z.object({
  externalId: z.string().min(1),
  date: z.string(),
  state: z.string().min(1).max(50),
  proposalCount: z.number().int().nonnegative().default(0),
  ownerExternalId: z.string().min(1),
});

export const snowflakeItemSchema = z.object({
  externalId: z.string().min(1),
  number: z.string().min(1).max(255),
  description: z.string().optional(),
  category: z.string().max(255).optional(),
  unit: z.string().max(50).optional(),
  state: z.string().min(1).max(50),
  ownerExternalId: z.string().min(1),
});

export const snowflakeProposalSchema = z.object({
  externalId: z.string().min(1),
  callNumber: z.string().min(1).max(255),
  completionCalendarDays: z.number().int().optional(),
  completionDate: z.string().optional(),
  completionDateIsDeduced: z.boolean().optional(),
  completionDateSource: z.string().max(255).optional(),
  completionWorkingDays: z.number().int().optional(),
  contractId: z.string().max(255).optional(),
  contractType: z.string().max(255).optional(),
  costEstimate: z.string().optional(),
  description: z.string().optional(),
  lettingDate: z.string().optional(),
  projectNumber: z.string().max(255).optional(),
  route: z.string().max(255).optional(),
  state: z.string().min(1).max(50),
  bidderCount: z.number().int().optional(),
  hasAwarded: z.boolean().default(false),
  hasBidTabulation: z.boolean().default(false),
  lettingExternalId: z.string().min(1),
  ownerExternalId: z.string().min(1),
});

export const snowflakeProposalItemSchema = z.object({
  externalId: z.string().min(1),
  proposalExternalId: z.string().min(1),
  itemExternalId: z.string().min(1),
  lineNumber: z.number().int().optional(),
  itemNumber: z.string().max(255).optional(),
  quantity: z.string(),
  costEstimate: z.string().optional(),
  description: z.string().optional(),
  state: z.string().min(1).max(50),
});

export const snowflakeBidSchema = z.object({
  externalId: z.string().min(1),
  vendorExternalId: z.string().min(1),
  proposalExternalId: z.string().min(1),
  deviation: z.string().optional(),
  value: z.string(),
  winner: z.boolean().default(false),
  rank: z.number().int().optional(),
  extensionTotal: z.string().optional(),
  timeAdjustment: z.string().optional(),
  evaluationAdjustment: z.string().optional(),
  adjustedTotal: z.string().optional(),
  deviationPercent: z.string().optional(),
  state: z.string().min(1).max(50),
});

export const snowflakeBidItemSchema = z.object({
  externalId: z.string().min(1),
  bidExternalId: z.string().min(1),
  itemExternalId: z.string().min(1),
  quantity: z.string(),
  lineNumber: z.number().int(),
  unitPrice: z.string(),
  extension: z.string(),
  state: z.string().min(1).max(50),
});

const nullableString = z.string().nullable().optional();
const nullableBool = z.boolean().nullable().optional();
const nullableNumber = z.number().nullable().optional();

export const snowflakePayEstimateSchema = z.object({
  externalId: z.string().min(1),
  proposalExternalId: nullableString,
  vendorExternalId: nullableString,
  globalVendorExternalId: nullableString,
  stateExternalId: nullableString,
  ownerExternalId: z.string().min(1),
  hasBidTabContract: nullableBool,
  contractId: nullableString,
  state: nullableString,
  estimateNumber: nullableNumber,
  estimateType: nullableString,
  contractorName: nullableString,
  periodStart: nullableString,
  periodEnd: nullableString,
  reportGeneratedDate: nullableString,
  lettingDate: nullableString,
  awardedDate: nullableString,
  ntpDate: nullableString,
  originalCompletionDate: nullableString,
  currentCompletionDate: nullableString,
  actualCompletionDate: nullableString,
  originalContractAmount: nullableString,
  netChangeOrderAmount: nullableString,
  currentContractAmount: nullableString,
  pctComplete: nullableString,
  thisEstimatePostedItems: nullableString,
  thisEstimateAdjustments: nullableString,
  thisEstimatePayable: nullableString,
  previousTotal: nullableString,
  toDateTotal: nullableString,
  incentive: nullableString,
  disincentive: nullableString,
  liquidatedDamage: nullableString,
  otherContractAdjustments: nullableString,
  storedMaterialsThisEstimate: nullableString,
  storedMaterialsToDate: nullableString,
  sourceFile: nullableString,
});

export const snowflakePayEstimateItemSchema = z.object({
  externalId: z.string().min(1),
  proposalExternalId: nullableString,
  itemExternalId: nullableString,
  unitExternalId: nullableString,
  ownerExternalId: z.string().min(1),
  stableKey: nullableString,
  state: nullableString,
  contractId: nullableString,
  estimateNumber: nullableNumber,
  projectNumber: nullableString,
  category: nullableString,
  lineNumber: nullableString,
  itemCode: nullableString,
  itemDescription: nullableString,
  ownerItemDescription: nullableString,
  numberNormalized: nullableString,
  categoryCode: nullableString,
  categoryName: nullableString,
  isTrueLumpSum: nullableBool,
  rawUnit: nullableString,
  normalizedUnit: nullableString,
  unitPrice: nullableString,
  bidQuantity: nullableString,
  netChangeOrderQty: nullableString,
  totalCurrentQuantity: nullableString,
  totalPostedQty: nullableString,
  qtyThisPeriod: nullableString,
  authorizedQuantity: nullableString,
  totalValueToDate: nullableString,
  isChangeOrder: nullableBool,
  sourceFile: nullableString,
});

export const snowflakeStateSchema = z.object({
  externalId: z.string().min(1),
  name: z.string().min(1).max(255),
  abbreviation: z.string().min(1).max(10),
  ownerExternalId: z.string().min(1),
});

export const snowflakeSyncPayloadSchema = z.object({
  owners: z.array(snowflakeOwnerSchema),
  vendors: z.array(snowflakeVendorSchema),
  lettings: z.array(snowflakeLettingSchema),
  items: z.array(snowflakeItemSchema),
  proposals: z.array(snowflakeProposalSchema),
  proposalItems: z.array(snowflakeProposalItemSchema),
  bids: z.array(snowflakeBidSchema),
  bidItems: z.array(snowflakeBidItemSchema),
});
